'use client';

import React from 'react';
import { Button, Tooltip } from '@heroui/react';
import {
  FilterSelect,
  ModelSearchSelect,
  FilterOption,
  ResponsiveTooltip,
} from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import { ResetFiltersIc, AccountIcon, FilterSourceCircleIc } from '../../../../components/icons';
import { getGroupingOptions, getLimitOptions, getSourceOptions } from '../constants';
import { FiltersProps } from '../types';
import CampaignSearchSelect from './CampaignSearchSelect';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  availableModels,
  availableCampaigns,
  availableCplCampaigns,
  isLoading,
  isCampaignsLoading,
  isCplCampaignsLoading,
  hasMoreCampaigns,
  hasMoreCplCampaigns,
  campaignsTotalCount,
  cplCampaignsTotalCount,
  hasActiveFilters,
  onFilterChange,
  onResetFilters,
  onModelSearchChange,
  onLoadMoreCampaigns,
  onLoadMoreCplCampaigns,
  onSearchCampaigns,
}) => {
  const { t } = useTranslations();

  const modelOptions: FilterOption[] = availableModels.map((model) => ({
    key: model.id,
    avatar: model.avatar,
    username: model.username,
    name: model.name,
    label: `${model.name} (@${model.username})`,
  }));

  // Info icon for limit filter tooltip
  const LimitInfoIcon = () => (
    <ResponsiveTooltip content={t('partner.revshare.income.filters.limitTooltip')} placement="top">
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="cursor-pointer"
      >
        <path
          d="M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.10051 9.8995 1.75 7 1.75C4.10051 1.75 1.75 4.10051 1.75 7C1.75 9.8995 4.10051 12.25 7 12.25Z"
          stroke="#A8A8B0"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7 9.33333V7"
          stroke="#A8A8B0"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M7 4.66667H7.00583"
          stroke="#A8A8B0"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </ResponsiveTooltip>
  );

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-4 md:flex-wrap max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
        {/* Account filter */}
        <ModelSearchSelect
          placeholder={t('partner.revshare.income.filters.account')}
          options={modelOptions}
          selectedKey={filters.account}
          onSelectionChange={(value) => onFilterChange('account', value)}
          onSearchChange={onModelSearchChange}
          isLoading={isLoading}
          startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
          variant="campaign"
          autoWidth={true}
          t={t}
        />

        {/* Campaign filter */}
        <CampaignSearchSelect
          placeholder={t('partner.revshare.income.filters.campaign')}
          campaigns={availableCampaigns}
          cplCampaigns={availableCplCampaigns}
          selectedKey={filters.campaign}
          onSelectionChange={(value) => onFilterChange('campaign', value)}
          isLoading={isCampaignsLoading}
          isCplLoading={isCplCampaignsLoading}
          hasMoreCampaigns={hasMoreCampaigns}
          hasMoreCplCampaigns={hasMoreCplCampaigns}
          campaignsTotalCount={campaignsTotalCount}
          cplCampaignsTotalCount={cplCampaignsTotalCount}
          onLoadMoreCampaigns={onLoadMoreCampaigns}
          onLoadMoreCplCampaigns={onLoadMoreCplCampaigns}
          onSearchCampaigns={onSearchCampaigns}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.66602 10.0013C1.66602 6.07293 1.66602 4.10875 2.8864 2.88836C4.10679 1.66797 6.07098 1.66797 9.99935 1.66797C13.9277 1.66797 15.8919 1.66797 17.1123 2.88836C18.3327 4.10875 18.3327 6.07293 18.3327 10.0013C18.3327 13.9297 18.3327 15.8939 17.1123 17.1142C15.8919 18.3346 13.9277 18.3346 9.99935 18.3346C6.07098 18.3346 4.10679 18.3346 2.8864 17.1142C1.66602 15.8939 1.66602 13.9297 1.66602 10.0013Z"
                stroke="#A8A8B0"
              />
              <path d="M5.83398 15V7.5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M10 15V5" stroke="#A8A8B0" strokeLinecap="round" />
              <path d="M14.166 14.9987V10.832" stroke="#A8A8B0" strokeLinecap="round" />
            </svg>
          }
        />

        {/* Grouping filter */}
        <FilterSelect
          placeholder={t('partner.revshare.income.filters.grouping')}
          options={getGroupingOptions(t)}
          selectedKey={filters.grouping}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('grouping', value)}
          showPlaceholderWithValue={true}
          startContent={
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.08398 5.41536C2.08398 3.84402 2.08398 3.05834 2.57214 2.57019C3.0603 2.08203 3.84597 2.08203 5.41732 2.08203C6.98867 2.08203 7.77434 2.08203 8.2625 2.57019C8.75065 3.05834 8.75065 3.84402 8.75065 5.41536V14.582C8.75065 16.1534 8.75065 16.9391 8.2625 17.4272C7.77434 17.9154 6.98867 17.9154 5.41732 17.9154C3.84597 17.9154 3.0603 17.9154 2.57214 17.4272C2.08398 16.9391 2.08398 16.1534 2.08398 14.582V5.41536Z"
                stroke="#A8A8B0"
              />
              <path
                d="M11.25 12.9154C11.25 11.344 11.25 10.5583 11.7382 10.0702C12.2263 9.58203 13.012 9.58203 14.5833 9.58203C16.1547 9.58203 16.9404 9.58203 17.4285 10.0702C17.9167 10.5583 17.9167 11.344 17.9167 12.9154V14.582C17.9167 16.1534 17.9167 16.9391 17.4285 17.4272C16.9404 17.9154 16.1547 17.9154 14.5833 17.9154C13.012 17.9154 12.2263 17.9154 11.7382 17.4272C11.25 16.9391 11.25 16.1534 11.25 14.582V12.9154Z"
                stroke="#A8A8B0"
              />
              <path
                d="M11.25 4.58203C11.25 3.80546 11.25 3.41718 11.3769 3.11089C11.546 2.70251 11.8705 2.37806 12.2789 2.2089C12.5851 2.08203 12.9734 2.08203 13.75 2.08203H15.4167C16.1932 2.08203 16.5815 2.08203 16.8878 2.2089C17.2962 2.37806 17.6206 2.70251 17.7898 3.11089C17.9167 3.41718 17.9167 3.80546 17.9167 4.58203C17.9167 5.3586 17.9167 5.74689 17.7898 6.05317C17.6206 6.46155 17.2962 6.78601 16.8878 6.95516C16.5815 7.08203 16.1932 7.08203 15.4167 7.08203H13.75C12.9734 7.08203 12.5851 7.08203 12.2789 6.95516C11.8705 6.78601 11.546 6.46155 11.3769 6.05317C11.25 5.74689 11.25 5.3586 11.25 4.58203Z"
                stroke="#A8A8B0"
              />
            </svg>
          }
          autoWidth={true}
        />

        {/* Limit filter */}
        {/* <FilterSelect
          placeholder={t('partner.revshare.income.filters.limit')}
          options={getLimitOptions(t)}
          selectedKey={filters.limit}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('limit', value === 'all' ? '' : value)}
          startContent={
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.99935 10.0013L7.5346 12.4421C5.09919 14.8538 3.88149 16.0597 4.22239 17.1001C4.2517 17.1895 4.28809 17.2765 4.33122 17.3602C4.83307 18.3346 6.55516 18.3346 9.99935 18.3346C13.4435 18.3346 15.1656 18.3346 15.6675 17.3602C15.7106 17.2765 15.747 17.1895 15.7763 17.1001C16.1172 16.0597 14.8995 14.8538 12.4641 12.4421L9.99935 10.0013ZM9.99935 10.0013L12.4641 7.56052C14.8995 5.1488 16.1172 3.94294 15.7763 2.90254C15.747 2.81311 15.7106 2.72611 15.6675 2.64235C15.1656 1.66797 13.4435 1.66797 9.99935 1.66797C6.55516 1.66797 4.83307 1.66797 4.33122 2.64235C4.28809 2.72611 4.2517 2.81311 4.22239 2.90254C3.88149 3.94294 5.09919 5.1488 7.5346 7.56053L9.99935 10.0013Z" stroke="#A8A8B0" strokeWidth="1.5"/>
            </svg>
          }
          endContent={<LimitInfoIcon />}
          autoWidth={true}
        /> */}

        {/* Source filter */}
        <FilterSelect
          placeholder={t('partner.revshare.income.filters.source')}
          options={getSourceOptions(t)}
          selectedKey={filters.source}
          variant="campaign"
          onSelectionChange={(value) => onFilterChange('source', value === 'all' ? '' : value)}
          startContent={<FilterSourceCircleIc />}
          autoWidth={true}
        />

        {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
        {/* Reset filters button */}
        {hasActiveFilters && (
          <Tooltip content={t('partner.revshare.offers.filters.resetTooltip')} placement="top">
            <Button
              //@ts-ignore
              size="xs"
              className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
              onPress={onResetFilters}
            >
              <ResetFiltersIc />
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default FiltersSection;
